import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { paymentsApi } from '../../api/payments';
import { PAGE_SIZE } from '../../constants';
import { formatCurrency, formatDate, paginate, matchesSearch } from '../../utils';
import type { Payment } from '../../types';

const PaymentsPage: React.FC = () => {
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await paymentsApi.getJobPayments({ page: 1, limit: 1000 });
      setRows(data.payments);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const q = search.trim();
      return !q || matchesSearch(row.id, q);
    });
  }, [rows, search]);

  const paged = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const columns: Column<Payment>[] = [
    {
      id: 'id',
      label: 'Transaction ID',
      minWidth: 120,
      render: (row) => (
        <Typography fontWeight={600} fontSize={13}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'jobId',
      label: 'Job ID',
      render: (row) => row.jobId,
    },
    {
      id: 'customer',
      label: 'Customer',
      minWidth: 140,
      render: (row) => row.customerName,
    },
    {
      id: 'provider',
      label: 'Service Provider',
      minWidth: 140,
      render: (row) => row.providerName,
    },
    {
      id: 'amount',
      label: 'Amount',
      align: 'right',
      render: (row) => formatCurrency(row.amount),
    },
    {
      id: 'method',
      label: 'Method',
      render: (row) => row.method,
    },
    {
      id: 'date',
      label: 'Date',
      render: (row) => formatDate(row.date),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  return (
    <Box>
      <PageHeader title="Payments" subtitle="View marketplace transactions" />

      <PageFilters
        search={search}
        searchPlaceholder="Search by Transaction ID…"
        onSearchChange={setSearch}
      />

      <DataTable
        columns={columns}
        rows={paged}
        loading={loading}
        getRowId={(row) => row.id}
        emptyTitle="No payments found"
        emptyDescription="Try a different Transaction ID."
      />

      <TablePaginationBar
        page={page}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onChange={setPage}
      />
    </Box>
  );
};

export default PaymentsPage;
